import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { 
  ChevronLeft, 
  CheckCircle, 
  Clock, 
  RotateCcw,
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  AlertCircle
} from 'lucide-react'
import { toast } from 'sonner'

interface Stage {
  id: string
  stage: number
  title: string
  description: string
  status: string
  duration: string
}

type StageFormData = Omit<Stage, 'id'>

export default function StagesManager() {
  const { token } = useAuth()
  const [stages, setStages] = useState<Stage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  
  // Estados para modais
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null)
  
  // Estado do formulário
  const [formData, setFormData] = useState<StageFormData>({
    stage: 0,
    title: '',
    description: '',
    status: 'a-iniciar',
    duration: ''
  })

  useEffect(() => {
    loadStages()
  }, [token])

  const loadStages = async () => {
    if (!token) {
      console.log('Sem token, não é possível carregar')
      return
    }
    setIsLoading(true)
    try {
      console.log('Carregando fases da API...')
      const data = await api.getAllStages()
      console.log('Fases carregadas:', data)
      setStages(data)
    } catch (error) {
      console.error('Erro ao carregar fases:', error)
      toast.error('Erro ao carregar fases')
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = async (stageId: string, newStatus: string) => {
    if (!token) return
    setIsSaving(true)
    try {
      console.log(`Atualizando fase ${stageId} para status: ${newStatus}`)
      const updated = await api.updateStage(token, stageId, { status: newStatus })
      console.log('Fase atualizada:', updated)
      toast.success(`Fase ${updated.stage} atualizada com sucesso!`)
      await loadStages()
      
      // 🔥 DISPARA EVENTO PARA O FRONTEND PÚBLICO SABER QUE HOUVE MUDANÇA
      window.dispatchEvent(new CustomEvent('stages-updated'))
      
    } catch (error) {
      console.error('Erro ao atualizar fase:', error)
      toast.error('Erro ao atualizar fase')
    } finally {
      setIsSaving(false)
    }
  }

  // ========== CRUD OPERATIONS ==========

  const handleCreateStage = async () => {
    if (!token) return
    
    // Validações
    if (!formData.stage || formData.stage <= 0) {
      toast.error('Número da fase deve ser maior que 0')
      return
    }
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }
    if (!formData.description.trim()) {
      toast.error('Descrição é obrigatória')
      return
    }
    if (!formData.duration.trim()) {
      toast.error('Duração é obrigatória')
      return
    }

    // Verificar se já existe fase com este número
    if (stages.some(s => s.stage === formData.stage)) {
      toast.error(`Já existe uma fase com o número ${formData.stage}`)
      return
    }

    setIsSaving(true)
    try {
      const newStage = await api.createStage(token, formData)
      toast.success(`Fase ${newStage.stage} criada com sucesso!`)
      setIsModalOpen(false)
      resetForm()
      await loadStages()
      window.dispatchEvent(new CustomEvent('stages-updated'))
    } catch (error: any) {
      console.error('Erro ao criar fase:', error)
      toast.error(error.message || 'Erro ao criar fase')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEditStage = async () => {
    if (!token || !selectedStage) return

    // Validações
    if (!formData.stage || formData.stage <= 0) {
      toast.error('Número da fase deve ser maior que 0')
      return
    }
    if (!formData.title.trim()) {
      toast.error('Título é obrigatório')
      return
    }
    if (!formData.description.trim()) {
      toast.error('Descrição é obrigatória')
      return
    }
    if (!formData.duration.trim()) {
      toast.error('Duração é obrigatória')
      return
    }

    // Verificar se já existe outra fase com este número
    if (stages.some(s => s.stage === formData.stage && s.id !== selectedStage.id)) {
      toast.error(`Já existe uma fase com o número ${formData.stage}`)
      return
    }

    setIsSaving(true)
    try {
      const updated = await api.updateStage(token, selectedStage.id, formData)
      toast.success(`Fase ${updated.stage} atualizada com sucesso!`)
      setIsEditModalOpen(false)
      resetForm()
      await loadStages()
      window.dispatchEvent(new CustomEvent('stages-updated'))
    } catch (error: any) {
      console.error('Erro ao atualizar fase:', error)
      toast.error(error.message || 'Erro ao atualizar fase')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteStage = async () => {
    if (!token || !selectedStage) return

    setIsSaving(true)
    try {
      await api.deleteStage(token, selectedStage.id)
      toast.success(`Fase ${selectedStage.stage} removida com sucesso!`)
      setIsDeleteModalOpen(false)
      setSelectedStage(null)
      await loadStages()
      window.dispatchEvent(new CustomEvent('stages-updated'))
    } catch (error: any) {
      console.error('Erro ao remover fase:', error)
      toast.error(error.message || 'Erro ao remover fase')
    } finally {
      setIsSaving(false)
    }
  }

  const resetForm = () => {
    setFormData({
      stage: 0,
      title: '',
      description: '',
      status: 'a-iniciar',
      duration: ''
    })
    setSelectedStage(null)
  }

  const openEditModal = (stage: Stage) => {
    setSelectedStage(stage)
    setFormData({
      stage: stage.stage,
      title: stage.title,
      description: stage.description,
      status: stage.status,
      duration: stage.duration
    })
    setIsEditModalOpen(true)
  }

  const openDeleteModal = (stage: Stage) => {
    setSelectedStage(stage)
    setIsDeleteModalOpen(true)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'em-andamento':
        return { bg: 'bg-[#FACC1F]/20 text-[#FACC1F]', icon: <Clock size={12} className="mr-1 animate-pulse" />, label: 'Em andamento' }
      case 'concluido':
        return { bg: 'bg-green-100 text-green-700', icon: <CheckCircle size={12} className="mr-1" />, label: 'Concluído' }
      default:
        return { bg: 'bg-gray-100 text-gray-500', icon: null, label: 'A iniciar' }
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D71C3] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando fases...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
              <ChevronLeft size={20} />
            </a>
            <div>
              <h1 className="text-xl font-serif font-bold text-gray-900">Gerenciar Fases do Projeto</h1>
              <p className="text-sm text-gray-500">Gerencie as fases do projeto, crie novas ou edite as existentes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                resetForm()
                setIsModalOpen(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D71C3] text-white rounded-lg hover:bg-[#175FA3] transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Nova Fase
            </button>
            <button
              onClick={loadStages}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-[#1D71C3] transition-colors"
            >
              <RotateCcw size={14} className={isLoading ? 'animate-spin' : ''} />
              Recarregar
            </button>
          </div>
        </div>
      </div>

      {/* LISTA DE FASES */}
      <div className="p-6 max-w-4xl mx-auto">
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
          
          <div className="space-y-8">
            {stages.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                <p className="text-gray-500">Nenhuma fase cadastrada.</p>
                <p className="text-sm text-gray-400 mt-1">Clique em "Nova Fase" para criar uma.</p>
              </div>
            ) : (
              stages.map((stage) => {
                const badge = getStatusBadge(stage.status)
                const isEmAndamento = stage.status === 'em-andamento'
                const isConcluido = stage.status === 'concluido'
                
                return (
                  <div key={stage.id} className="relative flex gap-5 group">
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 border-4 border-white shadow-md ${
                      isEmAndamento ? 'bg-[#1D71C3] ring-4 ring-[#1D71C3]/20' :
                      isConcluido ? 'bg-green-500' : 'bg-gray-300'
                    }`}>
                      <span className="text-white font-bold text-sm">{stage.stage}</span>
                    </div>
                    
                    <div className="flex-1 pb-6">
                      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-serif font-bold text-lg text-gray-900 mb-1">{stage.title}</h3>
                            <p className="text-sm text-gray-600 mb-3">{stage.description}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                              <Clock size={12} />
                              <span>{stage.duration}</span>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg}`}>
                                {badge.icon}
                                {badge.label}
                              </span>
                              
                              <div className="flex gap-2 ml-auto">
                                <button
                                  onClick={() => handleStatusChange(stage.id, 'a-iniciar')}
                                  disabled={isSaving || stage.status === 'a-iniciar'}
                                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                    stage.status === 'a-iniciar'
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  Pendente
                                </button>
                                <button
                                  onClick={() => handleStatusChange(stage.id, 'em-andamento')}
                                  disabled={isSaving || stage.status === 'em-andamento'}
                                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                    stage.status === 'em-andamento'
                                      ? 'bg-[#FACC1F]/20 text-[#FACC1F] cursor-not-allowed'
                                      : 'bg-[#FACC1F]/10 text-[#FACC1F] hover:bg-[#FACC1F]/20'
                                  }`}
                                >
                                  Em andamento
                                </button>
                                <button
                                  onClick={() => handleStatusChange(stage.id, 'concluido')}
                                  disabled={isSaving || stage.status === 'concluido'}
                                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                    stage.status === 'concluido'
                                      ? 'bg-green-100 text-green-500 cursor-not-allowed'
                                      : 'bg-green-50 text-green-600 hover:bg-green-100'
                                  }`}
                                >
                                  Concluído
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Botões de ação */}
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => openEditModal(stage)}
                              className="p-1.5 text-gray-400 hover:text-[#1D71C3] hover:bg-[#1D71C3]/10 rounded-lg transition-colors"
                              title="Editar fase"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => openDeleteModal(stage)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remover fase"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>ℹ Importante:</strong> Ao marcar uma fase como "Em andamento", as demais fases serão automaticamente alteradas para "A iniciar".
            Isso garante que apenas uma fase esteja ativa por vez no frontend.
          </p>
          <p className="text-sm text-blue-700 mt-2">
            <strong>💡 Dica:</strong> Use os botões de edição (✏️) e exclusão (🗑️) para gerenciar as fases. 
            A ordem das fases é definida pelo número (1, 2, 3, ...).
          </p>
        </div>
      </div>

      {/* MODAL - CRIAR FASE */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-gray-900">Nova Fase</h2>
              <button
                onClick={() => { setIsModalOpen(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número da Fase *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.stage || ''}
                  onChange={(e) => setFormData({ ...formData, stage: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                  placeholder="Ex: 1, 2, 3..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                  placeholder="Ex: Planejamento e Estudos"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                  placeholder="Descreva detalhadamente esta fase do projeto"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duração Estimada *</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                  placeholder="Ex: 8 meses, 3 meses, 1 ano..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                >
                  <option value="a-iniciar">A iniciar</option>
                  <option value="em-andamento">Em andamento</option>
                  <option value="concluido">Concluído</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setIsModalOpen(false); resetForm(); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateStage}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#1D71C3] text-white rounded-lg hover:bg-[#175FA3] transition-colors disabled:opacity-60"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Criar Fase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL - EDITAR FASE */}
      {isEditModalOpen && selectedStage && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-gray-900">Editar Fase</h2>
              <button
                onClick={() => { setIsEditModalOpen(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número da Fase *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.stage || ''}
                  onChange={(e) => setFormData({ ...formData, stage: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duração Estimada *</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1D71C3] focus:border-transparent"
                >
                  <option value="a-iniciar">A iniciar</option>
                  <option value="em-andamento">Em andamento</option>
                  <option value="concluido">Concluído</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                onClick={() => { setIsEditModalOpen(false); resetForm(); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditStage}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#1D71C3] text-white rounded-lg hover:bg-[#175FA3] transition-colors disabled:opacity-60"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Salvar Alterações
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL - CONFIRMAR EXCLUSÃO */}
      {isDeleteModalOpen && selectedStage && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-gray-900">Confirmar Exclusão</h2>
              <button
                onClick={() => { setIsDeleteModalOpen(false); setSelectedStage(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex items-start gap-3 mb-6 p-4 bg-red-50 rounded-lg">
              <AlertCircle size={20} className="text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium">Tem certeza que deseja remover esta fase?</p>
                <p className="text-sm text-red-700 mt-1">
                  <strong>Fase {selectedStage.stage}:</strong> {selectedStage.title}
                </p>
                <p className="text-xs text-red-600 mt-2">Esta ação não pode ser desfeita.</p>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setIsDeleteModalOpen(false); setSelectedStage(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteStage}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Removendo...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Remover Fase
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}